/**
 * Discovery search (/search): what a query looks like, and the viewer's recent searches (this device only).
 */

export type QueryShape =
  | { kind: 'empty' }
  | { kind: 'address'; address: `0x${string}` }
  | { kind: 'tx'; hash: `0x${string}` }
  | { kind: 'handle'; handle: string }
  | { kind: 'text'; text: string }

/** `0x` + 40 hex is a wallet or token, `0x` + 64 hex a transaction, `@name` a handle, anything else free text. */
export function shapeOf(raw: string): QueryShape {
  const q = raw.trim()
  if (!q) return { kind: 'empty' }
  if (/^0x[0-9a-fA-F]{40}$/.test(q)) return { kind: 'address', address: q.toLowerCase() as `0x${string}` }
  if (/^0x[0-9a-fA-F]{64}$/.test(q)) return { kind: 'tx', hash: q.toLowerCase() as `0x${string}` }
  const h = q.replace(/^@/, '').toLowerCase()
  if (q.startsWith('@') && /^[a-z0-9_]{3,24}$/.test(h)) return { kind: 'handle', handle: h }
  return { kind: 'text', text: q }
}

/** The text a handle lookup can try for a free-text query (`drift` → `drift`), or null when it cannot be a handle. */
export function handleCandidate(shape: QueryShape): string | null {
  if (shape.kind === 'handle') return shape.handle
  if (shape.kind !== 'text') return null
  const h = shape.text.toLowerCase()
  return /^[a-z0-9_]{3,24}$/.test(h) ? h : null
}

/** Case-insensitive match of a token card against free text: symbol (with or without `$`), name or agent handle. */
export function tokenMatches(t: { symbol: string; name: string; agent: { handle: string; name: string } }, text: string): boolean {
  const q = text.trim().replace(/^\$/, '').toLowerCase()
  if (!q) return false
  return [t.symbol, t.name, t.agent.handle, t.agent.name].some((v) => v.toLowerCase().includes(q))
}

export const RECENT_KEY = 'algoboo.search.recent'
const RECENT_MAX = 8

export function readRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length <= 120).slice(0, RECENT_MAX) : []
  } catch {
    return []
  }
}

/** `list` with `q` first, without duplicates (case-insensitive), at most RECENT_MAX long. */
export function withRecent(list: readonly string[], q: string): string[] {
  const v = q.trim()
  if (!v) return [...list]
  return [v, ...list.filter((x) => x.toLowerCase() !== v.toLowerCase())].slice(0, RECENT_MAX)
}

/** Remember `q` first, without duplicates (case-insensitive). Storage failures are ignored. */
export function pushRecent(q: string): string[] {
  const v = q.trim()
  if (!v) return readRecent()
  const next = withRecent(readRecent(), v)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    // storage blocked: recent searches last for this page only
  }
  return next
}

export function clearRecent(): void {
  try {
    localStorage.removeItem(RECENT_KEY)
  } catch {
    // nothing to clear
  }
}
